/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect } from 'react';
import { Form } from '../../molecules/PatternForm';
import StatusToast from 'components/molecules/StatusToast/StatusToast';
import { getAllRules, getAllChutes, updateTemplate, getTemplateDetail } from '../../../services/templateService';
import { Chute } from 'interfaces/templates';
import { useNavigate } from 'react-router-dom';
import { LoaderTransparent } from 'components/molecules/LoaderTransparent/LoaderTransparent';

interface Props {
  patternId: number;
}

interface Group {
  group_id?: number;
  chutes: number[];
}

interface PatternData {
  id: number;
  name: string;
  groups: {
    id: number;
    chutes: number[];
  }[];
}

export default function PatternEdit({ patternId }: Props) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [ruleOptions, setRuleOptions] = useState<{ value: string; label: string }[]>([]);
  const [baseChutes, setBaseChutes] = useState<Chute[]>([]);
  const [groups, setGroups] = useState<Group[]>([]);
  const [patternName, setPatternName] = useState('');
  const [toast, setToast] = useState<{ title: string; type: 'success' | 'error' | 'loading'; visible: boolean }>({
    title: '',
    type: 'success',
    visible: false,
  });
  const [patternData, setPatternData] = useState<PatternData | null>(null);

  const showToast = (title: string, type: 'success' | 'error' | 'loading') => {
    setToast({ title, type, visible: true });
    setTimeout(() => {
      setToast(prev => ({ ...prev, visible: false }));
    }, 3000);
  };

  const handleAddRule = () => {
    setGroups(prev => [...prev, { chutes: [] }]);
  };

  const handleChutesSelected = (ruleIndex: number, selected: number[]) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[ruleIndex].chutes = selected;
      return updated;
    });
  };

  const handleDeleteRule = (groupIdToDelete?: number) => {
    const updatedGroups = groups.filter(g => g.group_id !== groupIdToDelete);
    setGroups(updatedGroups);
  };

  const handleUpdateGroup = (ruleIndex: number, group_id: number, chutes: number[]) => {
    setGroups(prev => {
      const updated = [...prev];
      updated[ruleIndex] = { group_id, chutes };
      return updated;
    });
  };

  const transformGroupsToPayload = (): { group_id: number; chutes: number[] }[] => {
    return groups
      .filter((g): g is { group_id: number; chutes: number[] } => typeof g.group_id === 'number')
      .map(group => ({
        group_id: group.group_id,
        chutes: group.chutes
          .map(id => baseChutes.find(c => c.id === id)?.chute)
          .filter((chute): chute is number => chute !== undefined),
      }));
  };

  const handleEdit = async () => {
    if (!patternName.trim()) {
      return showToast('El nombre de la plantilla es requerido', 'error');
    }

    if (groups.length === 0 || groups.some(g => g.chutes.length === 0)) {
      return showToast('Todas las reglas deben tener al menos un chute asignado', 'error');
    }

    try {
      const formData = {
        name: patternName.trim(),
        groups: transformGroupsToPayload(),
      };

      await updateTemplate(patternId, formData);

      showToast('Plantilla actualizada con éxito', 'success');
      navigate(`/templates/show/${patternId}`);
    } catch (e: any) {
      showToast( e.message ? e.message : 'Error al actualizar la plantilla', 'error');
    }
  };

  const buttonValidate = () => {
    return (!patternName.trim() || groups.length === 0 || groups.some(g => g.chutes.length === 0))
  }

  const renderRuleItems = () =>
    groups.map((group, index) => {
      const defaultChutes = group.chutes
        .map(chute => {
          const match = baseChutes.find(c => c.chute === chute);
          return match ? { chute: match.chute, id: match.id } : null;
        })
        .filter((c): c is { chute: number; id: number } => c !== null);

      const disabledRuleIds = groups
        .map(g => g.group_id)
        .filter((gid): gid is number => typeof gid === 'number')
        .filter((gid, i) => i !== index);

      const assignedChutes = groups.flatMap((g, i) => (i === index ? [] : g.chutes));

      return (
        <React.Fragment key={group.group_id ?? `new-${index}`}>
          <Form.RuleItem
            chutes={baseChutes}
            ruleOptions={ruleOptions}
            onDelete={() => handleDeleteRule(group.group_id)}
            ruleIndex={index}
            onChutesSelected={handleChutesSelected}
            selectedChutes={assignedChutes}
            onGroupUpdate={handleUpdateGroup}
            selectedRuleId={group.group_id?.toString()}
            defaultChutes={defaultChutes}
            disabledRuleIds={disabledRuleIds}
          />
          <hr />
        </React.Fragment>
      );
    });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [template, rules, chutes] = await Promise.all([
          getTemplateDetail(patternId),
          getAllRules(),
          getAllChutes('sorting'),
        ]);

        setPatternData(template);

        const options = rules?.groups?.map((g: any) => ({
          value: g.id.toString(),
          label: g.name,
        })) ?? [];
        setRuleOptions(options);

        const transformedChutes: Chute[] = (chutes.data?.default_chutes || []).map((item: any) => ({
          id: item.id,
          chute: item.number,
          type: 'available',
        }));
        setBaseChutes(transformedChutes);
      } catch {
        setRuleOptions([]);
        setBaseChutes([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [patternId]);

  useEffect(() => {
    if (patternData && baseChutes.length > 0 && groups.length === 0) {
      const initialGroups = patternData.groups.map(g => ({
        group_id: g.id,
        chutes: g.chutes,
      }));
      setGroups(initialGroups);
    }

    if (patternData?.name && patternName === '') {
      setPatternName(patternData.name);
    }
  }, [patternData, baseChutes]);

  if (loading || !patternData) return <LoaderTransparent />;

  return (
    <>
      <div className="h-screen w-full mx-5 flex flex-col">
        <div className="flex-1 min-h-0 overflow-auto">
          <Form
            onAddRule={handleAddRule}
            onEditTemplate={handleEdit}
            templateName={patternName}
            onNameChange={val => setPatternName(val)}
            buttonDisabled={buttonValidate()}
          >
            {renderRuleItems()}
          </Form>
        </div>

        {toast.visible && <StatusToast title={toast.title} type={toast.type} />}
      </div>
    </>
  );
}
